import { inject, Inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { InjectableSuperclass } from '@sama/ng-core';
import {
  getShapeForEventTracking,
  ShapeState,
} from '../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { selectionToArray } from '../../../../../cross-team-shared/misc-utils';
import { outputAnnotationInfoStoreToken } from '../../../../../outputs/annotation/annotation-shared/annotation-providers';
import { AbstractAnnotationStore } from '../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { removeDynamicTags } from '../../../../../outputs/annotation/annotation-shared/utils/tag-utils';
import { EventTrackingValue } from '../../../../../team-apis/core/event-tracking';
import { AbstractOutputWithAnswerOptions } from '../../../../../team-apis/otterdam/outputs/output';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../../h3ml/state/output-info';
import { FusionManager } from '../fusion/fusion-manager';
import { PushAndTrackService } from './push-and-track.service';
import { ShapeSelectionService } from './shape-selection.service';

export interface BulkRemoveKeyframesOptions {
  startFrameNumber: number;
  endFrameNumber: number;
}

@Injectable()
export class BulkRemoveFrameAnnotationsService extends InjectableSuperclass {
  private readonly outputId: number;
  private readonly shapesStore = inject(outputAnnotationInfoStoreToken)(
    'shapes',
  );

  constructor(
    private store: AbstractAnnotationStore,
    private pushAndTrackService: PushAndTrackService,
    @Inject(outputInfoStoreToken)
    private outputInfoStore: Store<OutputInfo<AbstractOutputWithAnswerOptions>>,
    private fusionManager: FusionManager,
    private selectionService: ShapeSelectionService,
  ) {
    super();
    this.outputId = this.outputInfoStore.state().output.id;
  }

  bulkRemoveKeyframes(
    source: string,
    options: BulkRemoveKeyframesOptions,
    notifyFusionManager = true,
  ) {
    const affectedShapes =
      this.removeKeyframesAndDynamicTagsForSelectedShapes(options);

    const commonEventValues = {
      method: 'bulk_remove',
      source,
      start_frame: options.startFrameNumber,
      end_frame: options.endFrameNumber,
      total_removed_dynamic_tags: affectedShapes.reduce(
        (total, shape) => total + shape.removed_dynamic_tags_count,
        0,
      ),
    };

    if (this.shouldSyncBulkRemove(notifyFusionManager)) {
      this.fusionManager.bulkRemoveKeyframes(options, this.outputId);
    }

    if (this.store.state().bulkRemovePreferences.includeKeyFrames) {
      this.pushEditShapePointsEvent(commonEventValues, affectedShapes);
    } else {
      this.pushToggleTagKeyframeEvent(commonEventValues, affectedShapes);
    }
  }

  private pushToggleTagKeyframeEvent(
    commonEventValues: {
      method: string;
      end_frame: number;
      start_frame: number;
      source: string;
      total_removed_dynamic_tags: number;
    },
    affectedShapes: EventTrackingValue[],
  ) {
    this.pushAndTrackService.pushAndTrack({
      id: 'toggle_tag_keyframe',
      bucket: 'tag_selection',
      ...commonEventValues,
      parent_context: {
        output_id: this.outputId,
        output_type: this.outputInfoStore.state().output.type,
        shapes: affectedShapes,
      },
    });
  }

  private pushEditShapePointsEvent(
    commonEventValues: {
      method: string;
      end_frame: number;
      start_frame: number;
      source: string;
      total_removed_dynamic_tags: number;
    },
    affectedShapes: EventTrackingValue[],
  ) {
    this.pushAndTrackService.pushAndTrack({
      id: 'edit_shape_points',
      bucket: 'adjustment',
      ...commonEventValues,
      shapes: affectedShapes,
      includes_tag: this.store.state().bulkRemovePreferences.includeDynamicTags,
      total_removed_keyframes: affectedShapes.reduce(
        (total, shape) => total + shape.removed_keyframes_count,
        0,
      ),
    });
  }

  private removeKeyframesAndDynamicTagsForSelectedShapes(
    options: BulkRemoveKeyframesOptions,
  ) {
    const affectedShapes: EventTrackingValue[] = [];

    this.store.batch(() => {
      this.getSelectionIndexes()
        .map((index: number) => this.shapesStore(index))
        .forEach((store) => {
          const removedData = this.removeKeyframesAndDynamicTags(
            // @ts-ignore -- fix me
            store,
            options,
          );

          if (
            removedData.keyframesRemovedCount ||
            removedData.dynamicTagsRemovedCount
          ) {
            const state = store.state();
            affectedShapes.push({
              // @ts-ignore -- fix me
              ...getShapeForEventTracking(state),
              removed_keyframes_count: removedData.keyframesRemovedCount,
              removed_dynamic_tags_count: removedData.dynamicTagsRemovedCount,
            });
          }
        });
    });
    return affectedShapes;
  }

  private removeKeyframesAndDynamicTags(
    store: Store<ShapeState>,
    options: BulkRemoveKeyframesOptions,
  ) {
    let keyframesRemovedCount = 0;
    if (this.store.state().bulkRemovePreferences.includeKeyFrames) {
      keyframesRemovedCount = this.removeKeyframes(
        store,
        options.startFrameNumber,
        options.endFrameNumber,
      );
    }

    let dynamicTagsRemovedCount = 0;
    if (this.store.state().bulkRemovePreferences.includeDynamicTags) {
      dynamicTagsRemovedCount = removeDynamicTags(
        store,
        options.startFrameNumber,
        options.endFrameNumber,
      );
    }

    return { keyframesRemovedCount, dynamicTagsRemovedCount };
  }

  // Remove all keyframes within the range, keeping at least the first or last
  // one for each shape
  private removeKeyframes(
    store: Store<ShapeState>,
    startFrameNumber: number,
    endFrameNumber: number,
  ) {
    const keyframes = store.state().key_locations;

    const keyframesToKeep = keyframes.filter(
      (keyframe) =>
        keyframe.frame_number < startFrameNumber ||
        keyframe.frame_number > endFrameNumber,
    );

    if (keyframesToKeep.length === keyframes.length) {
      return 0;
    }

    let keyframeToPreserveOnDeletion = keyframes[0];
    if (
      this.store.state().bulkRemovePreferences.keyframeToPreserveOnDeletion ===
      'last'
    ) {
      keyframeToPreserveOnDeletion = keyframes[keyframes.length - 1];
    }

    keyframesToKeep[0] ??= keyframeToPreserveOnDeletion;

    store('key_locations').set(keyframesToKeep);

    return keyframes.length - keyframesToKeep.length;
  }

  private shouldSyncBulkRemove(notifyFusionManager: boolean) {
    return (
      this.fusionManager.hasFusedAnswerOptions() &&
      notifyFusionManager &&
      this.store.state().bulkRemovePreferences.includeDynamicTags
    );
  }

  private getSelectionIndexes() {
    const selection = this.selectionService.getSelectionForOutput();
    return selectionToArray(selection);
  }
}
